// Add / Edit attribute modal for the Layout Config "Attributes" screen.
//
// Visual style matches the rest of the doit Layout Config area
// (LayoutEditorModal, the upload modal, etc.): compact labels, plain
// rounded-md inputs, blue accents, gray footer with right-aligned Cancel
// + primary button.
//
// Field set / behavior mirrors the reference's AddFieldIdentifier
// dialog (ocr-ui ... config-studio/field-identifier/AddFieldIdentifier.jsx):
//   - Field Identifier, Data Type *, Regex Pattern, Regex Description, Status *
//   - Data Type auto-fills Regex Pattern from DATA_TYPE_REGEX_MAP; the
//     pattern input is read-only unless Data Type === "Custom".
//   - Field Identifier is validated against
//     /fields_identifier_check_exists with a debounce; submit is blocked
//     while validation is in flight or has surfaced a duplicate.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  X,
  Plus,
  Save,
  Check,
  AlertCircle,
  Loader2,
  FileText,
} from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";
import type {
  AttributeRecord,
  AttributeSection,
} from "../../../types/documents";

export type AttributeFormMode = "add" | "edit";

interface AttributeFormModalProps {
  open: boolean;
  mode: AttributeFormMode;
  section: AttributeSection;
  /** Pre-populate when editing. */
  initial?: AttributeRecord | null;
  onClose: () => void;
  /** Fires on successful save so the parent can invalidate its list query. */
  onSaved: () => void;
}

interface FormState {
  identifier: string;
  type: string;
  pattern: string;
  description: string;
  status: string;
}

const EMPTY_FORM: FormState = {
  identifier: "",
  type: "",
  pattern: "",
  description: "",
  status: "Active",
};

// Mirrors reference constants.jsx — keep in sync if the backend ever
// adds more options.
const DATA_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: "Text", value: "Text" },
  { label: "Number", value: "Number" },
  { label: "Decimal", value: "Decimal" },
  { label: "Date", value: "Date" },
  { label: "Custom", value: "Custom" },
];

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" },
];

const DATA_TYPE_REGEX_MAP: Record<string, string> = {
  Text: "^[a-zA-Z\\s]*$",
  Number: "^[0-9]*$",
  Decimal: "^[0-9]*\\.?[0-9]*$",
  Date: "^\\d{4}-\\d{2}-\\d{2}$",
};

type ValidationState = "idle" | "validating" | "valid" | "invalid";

export function AttributeFormModal({
  open,
  mode,
  section,
  initial,
  onClose,
  onSaved,
}: AttributeFormModalProps) {
  const isEdit = mode === "edit";
  const isLineItem = section === "line_item";

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [validation, setValidation] = useState<ValidationState>("idle");
  const validationTimer = useRef<number | null>(null);

  // Hydrate the form whenever the modal (re)opens.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setValidation("idle");
    if (isEdit && initial) {
      setForm({
        identifier: initial.identifier ?? "",
        type: initial.type ?? "",
        pattern: initial.pattern ?? "",
        description: initial.description ?? "",
        status: initial.status ?? "Active",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, isEdit, initial]);

  // Close on Escape so the dialog doesn't trap the user.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Debounced uniqueness check for the Field Identifier field. Skips the
  // call when editing an existing row with an unchanged identifier
  // (otherwise it would always report "exists" against itself).
  const runIdentifierValidation = (value: string) => {
    if (validationTimer.current != null) {
      window.clearTimeout(validationTimer.current);
      validationTimer.current = null;
    }
    if (!value.trim()) {
      setValidation("idle");
      return;
    }
    if (isEdit && value === initial?.identifier) {
      setValidation("idle");
      return;
    }
    setValidation("validating");
    validationTimer.current = window.setTimeout(async () => {
      try {
        const res = await ocrApi.checkAttributeIdentifierExists({
          section,
          identifier: value,
        });
        if (res.exists) {
          setValidation("invalid");
          setErrors((prev) => ({
            ...prev,
            identifier: "Field Identifier already exists",
          }));
        } else {
          setValidation("valid");
          setErrors((prev) => {
            const { identifier: _i, ...rest } = prev;
            void _i;
            return rest;
          });
        }
      } catch {
        setValidation("idle");
      }
    }, 700);
  };

  useEffect(() => {
    return () => {
      if (validationTimer.current != null) {
        window.clearTimeout(validationTimer.current);
      }
    };
  }, []);

  const isCustom = form.type === "Custom";

  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => {
      // Auto-fill regex pattern from canonical map; clear it when the
      // user picks "Custom" so they have a clean slate.
      if (key === "type") {
        const autoPattern = DATA_TYPE_REGEX_MAP[value];
        return {
          ...prev,
          type: value,
          pattern: value === "Custom" ? "" : autoPattern ?? prev.pattern,
        };
      }
      return { ...prev, [key]: value };
    });
    setErrors((prev) => {
      const { [key]: _, ...rest } = prev;
      void _;
      return rest;
    });
    if (key === "identifier") {
      runIdentifierValidation(value);
    }
  };

  const validateForm = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.identifier.trim()) {
      next.identifier = "Field Identifier is required";
    }
    if (!form.type) next.type = "Data Type is required";
    if (isCustom && !form.pattern.trim()) {
      next.pattern = "Regex Pattern is required for Custom data type";
    }
    if (!form.status) next.status = "Status is required";
    setErrors((prev) => ({ ...prev, ...next }));
    return Object.keys(next).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        identifier: form.identifier.trim(),
        type: form.type,
        pattern: form.pattern,
        description: form.description,
        status: form.status,
      };
      if (isEdit) {
        return ocrApi.updateAttribute(section, {
          ...payload,
          id: String(initial?.id ?? ""),
        });
      }
      return ocrApi.insertAttribute(section, payload);
    },
    onSuccess: (data) => {
      const ok =
        data?.status === "success" || data?.message === "success";
      if (ok) {
        toast.success(
          isEdit
            ? "Attribute updated successfully"
            : "Attribute added successfully",
        );
        onSaved();
        onClose();
      } else {
        toast.error(
          data?.message ||
            data?.status ||
            `Failed to ${isEdit ? "update" : "add"} attribute.`,
        );
      }
    },
    onError: (err: unknown) => {
      toast.error("Save failed", {
        description:
          err instanceof Error ? err.message : "Please try again.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validation === "validating" || validation === "invalid") return;
    if (!validateForm()) return;
    saveMutation.mutate();
  };

  const headerTitle = useMemo(() => {
    if (isEdit) {
      return isLineItem
        ? "Edit line item attribute"
        : "Edit field item attribute";
    }
    return isLineItem
      ? "New line item attribute"
      : "New field item attribute";
  }, [isEdit, isLineItem]);

  const subHeaderTitle = useMemo(() => {
    if (isEdit) return "Update the attribute details below.";
    return isLineItem
      ? "Add a new identifier the line-item extraction can match against."
      : "Add a new identifier the field-item extraction can match against.";
  }, [isEdit, isLineItem]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={headerTitle}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-black/5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-blue-50 p-1.5">
              <FileText className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">
                {headerTitle}
              </h2>
              <p className="text-[11px] text-gray-500">{subHeaderTitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Body */}
        <div className="grid grid-cols-1 gap-4 px-5 py-4 sm:grid-cols-2">
          {/* Field Identifier */}
          <Field
            label="Field Identifier"
            required
            error={errors.identifier}
            hint={
              validation === "validating"
                ? "Checking availability…"
                : validation === "valid"
                  ? "Available"
                  : "Unique per section."
            }
            adornment={<IdentifierValidationIcon state={validation} />}
          >
            <input
              type="text"
              value={form.identifier}
              onChange={(e) => setField("identifier", e.target.value)}
              placeholder="e.g. Container No"
              className={inputClass(!!errors.identifier)}
            />
          </Field>

          {/* Data Type */}
          <Field label="Data Type" required error={errors.type}>
            <select
              value={form.type}
              onChange={(e) => setField("type", e.target.value)}
              className={inputClass(!!errors.type)}
            >
              <option value="">— Select data type —</option>
              {DATA_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          {/* Regex Pattern */}
          <Field
            label="Regex Pattern"
            error={errors.pattern}
            hint={
              isCustom
                ? "Custom regex — required."
                : form.type
                  ? "Auto-filled from the selected data type."
                  : "Pick a data type to auto-fill, or choose Custom."
            }
          >
            <input
              type="text"
              value={form.pattern}
              onChange={(e) => setField("pattern", e.target.value)}
              disabled={!isCustom && !!form.type}
              placeholder=".*"
              className={`${inputClass(!!errors.pattern)} font-mono disabled:bg-gray-50 disabled:text-gray-500`}
            />
          </Field>

          {/* Regex Description */}
          <Field label="Regex Description" error={errors.description}>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="e.g. Whole numbers only"
              className={inputClass(!!errors.description)}
            />
          </Field>

          {/* Status */}
          <Field label="Status" required error={errors.status}>
            <select
              value={form.status}
              onChange={(e) => setField("status", e.target.value)}
              className={inputClass(!!errors.status)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-5 py-3">
          {saveMutation.isError ? (
            <div className="mr-auto flex items-center gap-1 text-xs text-rose-600">
              <AlertCircle className="h-3.5 w-3.5" />
              {(saveMutation.error as Error)?.message ?? "Save failed"}
            </div>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            disabled={saveMutation.isPending}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              saveMutation.isPending ||
              validation === "validating" ||
              validation === "invalid"
            }
            className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isEdit ? (
              <Save className="h-3.5 w-3.5" />
            ) : (
              <Plus className="h-3.5 w-3.5" />
            )}
            {saveMutation.isPending
              ? "Saving…"
              : isEdit
                ? "Save changes"
                : "Add attribute"}
          </button>
        </footer>
      </form>
    </div>
  );
}

// -------------------------------------------------------------- helpers

function inputClass(hasError: boolean): string {
  return `w-full rounded-md border bg-white px-2.5 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-1 ${
    hasError
      ? "border-rose-300 focus:border-rose-500 focus:ring-rose-500"
      : "border-gray-200 focus:border-blue-500 focus:ring-blue-500"
  }`;
}

interface FieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  /** Small inline element shown on the right side of the label row. */
  adornment?: React.ReactNode;
  children: React.ReactNode;
}

function Field({
  label,
  required,
  hint,
  error,
  adornment,
  children,
}: FieldProps) {
  return (
    <div>
      <label className="mb-1 flex items-center justify-between text-xs font-medium text-gray-700">
        <span>
          {label}
          {required ? <span className="text-rose-500"> *</span> : null}
        </span>
        {error ? (
          <span className="text-rose-600">{error}</span>
        ) : (
          adornment
        )}
      </label>
      {children}
      {hint && !error ? (
        <p className="mt-1 text-[11px] text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

function IdentifierValidationIcon({ state }: { state: ValidationState }) {
  if (state === "validating") {
    return <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />;
  }
  if (state === "valid") {
    return <Check className="h-3.5 w-3.5 text-emerald-500" />;
  }
  if (state === "invalid") {
    return <AlertCircle className="h-3.5 w-3.5 text-rose-500" />;
  }
  return null;
}
